/**
 * 未認証時のログイン誘導。
 *
 * `docs/guidelines/frontend.md` の Critical 規約「未認証時は `/login` route または Edge Auth へ
 * full-page redirect する」に対応する。spec-console は `/login` route を持たないので Edge Auth
 * 側へ倒す。SPA 内遷移ではなくフルページ遷移にするのは、OAuth へのクロスオリジン redirect を
 * `fetch` が追従できないため（Edge Router Worker の `enforceAuth` が同じ理由で fetch には 302 では
 * なく 401 を返す）。
 *
 * 認証エラーと API 障害を混ぜないのが本モジュールの要点。401 だけを「ログインし直せば直る」と
 * 判定し、それ以外の失敗はエラー表示側に残す。全部 redirect に倒すと、API が落ちているときに
 * ログイン画面との間で無限に往復する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 認証エラーだけを判別し、Edge Auth の OAuth 開始 URL へフルページ遷移させる
 * @graph-connects buildEdgeAuthStartUrl [calls] Edge Auth の OAuth 開始 URL 組み立て
 */
import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { ORPCError } from '@orpc/client';

/**
 * 未認証（401）による失敗かを判定する。
 *
 * Edge Router Worker が API より手前で返す 401 と、API 自身が返す `UNAUTHORIZED` の
 * どちらも同じ意味なので、両方を拾う。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business API 呼び出しの失敗が未認証由来（401 / UNAUTHORIZED）かを判定する
 * @graph-connects none
 */
export function isUnauthenticatedError(error: unknown): boolean {
  if (!(error instanceof ORPCError)) return false;
  return error.code === 'UNAUTHORIZED' || error.status === 401;
}

/**
 * Edge Auth の OAuth 開始 URL へフルページ遷移する。戻り先は現在の URL。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 現在の URL を戻り先に指定して Edge Auth の OAuth 開始 URL へフルページ遷移する
 * @graph-connects buildEdgeAuthStartUrl [calls] Edge Auth の OAuth 開始 URL 組み立て
 */
export function redirectToEdgeAuth(): void {
  window.location.href = buildEdgeAuthStartUrl(window.location.href);
}
